import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LayoutHeader from '../../layout/LayoutHeader';
import { useDispatch } from 'react-redux';
import { getDataAPI } from '../../utils/fetchData';
import { getCachedData, refreshData } from '../../utils/dataPreloader';
import DizeeTable from '../../components/Sheets/DizeeTable';
import ConflictBanner from '../../components/Sync/ConflictBanner';

const TABS = [
  { id: 'bookings', label: 'Bookings' },
  { id: 'catalog', label: 'Catalog' },
  { id: 'touring', label: 'Touring' }
];

export default function SheetsHub() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [activeTab, setActiveTab] = useState('bookings');
  const [loading, setLoading] = useState(true);
  const [sheetsSettings, setSheetsSettings] = useState(null);
  const [bookings, setBookings] = useState(() => getCachedData('bookings') || []);
  const [catalog, setCatalog] = useState(() => getCachedData('catalog') || []);
  const [tourShows, setTourShows] = useState([]);
  const [conflictCount] = useState(0);

  const isConfigured = useMemo(() => {
    if (!sheetsSettings) return false;
    // Treat as configured if workspaceId + secret exist.
    return Boolean(sheetsSettings.workspaceId && sheetsSettings.secretToken);
  }, [sheetsSettings]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const token = JSON.parse(localStorage.getItem('dizeeToken'));
        const res = await getDataAPI('integrations/google-sheets/settings', token);
        if (res?.data?.status === 200) {
          setSheetsSettings(res.data.data || null);
        } else {
          setSheetsSettings(null);
        }

        // Load tab data in parallel (use cache immediately; refresh in background).
        const [bRes, cRes, tRes] = await Promise.allSettled([
          getDataAPI('user/getBookings', token),
          getDataAPI('user/getCatalog', token),
          getDataAPI('user/tm/shows', token)
        ]);

        if (bRes.status === 'fulfilled' && bRes.value?.data?.status === 200) {
          const raw = bRes.value.data.data;
          const rows = Array.isArray(raw) ? raw : raw?.data;
          if (Array.isArray(rows)) setBookings(rows);
          refreshData('bookings', 'user/getBookings', token);
        }

        if (cRes.status === 'fulfilled' && cRes.value?.data?.status === 200) {
          const raw = cRes.value.data.data;
          const rows = Array.isArray(raw) ? raw : raw?.data;
          if (Array.isArray(rows)) setCatalog(rows);
          refreshData('catalog', 'user/getCatalog', token);
        }

        if (tRes.status === 'fulfilled' && tRes.value?.data?.status === 200) {
          const raw = tRes.value.data.data;
          const rows = Array.isArray(raw) ? raw : [raw].filter(Boolean);
          if (Array.isArray(rows)) setTourShows(rows);
        }
      } catch (e) {
        console.error('Failed to load Google Sheets settings:', e);
        setSheetsSettings(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <LayoutHeader>
      <div className="flex justify-center w-full">
        <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">
          <div className="px-4 my-[50px] flex w-full justify-between items-center">
            <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>
              Sheets
            </span>
            <span
              className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70"
              onClick={() => navigate('/connectors/google-sheets')}
            >
              Manage sync
            </span>
          </div>

          <div className="w-full px-4 pb-20 overflow-y-auto flex-1">
            <ConflictBanner
              count={conflictCount}
              onReview={() => navigate('/connectors/google-sheets')}
            />
            {/* Status */}
            <div className="mb-4 p-4 bg-black border border-[rgba(255,255,255,0.1)] rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <p className="text-white text-[12px] font-medium">Workbook</p>
                {loading ? (
                  <span className="text-white text-[10px] opacity-60">Loading…</span>
                ) : isConfigured ? (
                  <span className="text-green-500 text-[10px] font-medium">Google sync enabled</span>
                ) : (
                  <span className="text-red-500 text-[10px] font-medium">Not connected</span>
                )}
              </div>
              <p className="text-white text-[11px] opacity-60 leading-relaxed">
                This is your Dizee-native workbook. Tabs are fed by Dizee apps and (when connected) synced with Google Sheets.
              </p>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 mb-4">
              {TABS.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setActiveTab(t.id)}
                  className={[
                    'flex-1 px-3 py-2 rounded-lg text-[11px] border transition-all',
                    activeTab === t.id
                      ? 'bg-[rgba(0,206,209,0.12)] border-[rgba(0,206,209,0.35)] text-white'
                      : 'bg-[rgba(255,255,255,0.03)] border-[rgba(255,255,255,0.08)] text-white opacity-70 hover:opacity-90'
                  ].join(' ')}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <div className="p-4 bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.06)] rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <p className="text-white text-[12px] font-medium">
                  {TABS.find((t) => t.id === activeTab)?.label} tab
                </p>
                <p className="text-white text-[10px] opacity-50">
                  {loading ? 'Loading…' : 'Edit from row actions'}
                </p>
              </div>

              {activeTab === 'bookings' && (
                <DizeeTable
                  rows={bookings}
                  rowKey={(r) => r?._id}
                  emptyTitle="No bookings found"
                  emptyBody="When you add dates in Bookings, they’ll appear here."
                  columns={[
                    {
                      key: '__edit',
                      header: '',
                      widthClass: 'min-w-[70px]',
                      render: (r) => (
                        <button
                          onClick={() => r?._id && navigate(`/bookings/edit/${r._id}`)}
                          className="text-[10px] text-[#00CED1] hover:opacity-80"
                        >
                          Edit
                        </button>
                      )
                    },
                    {
                      key: 'perf_date',
                      header: 'Date',
                      widthClass: 'min-w-[110px]',
                      render: (r) => {
                        const d = r?.perf_date ? new Date(r.perf_date) : null;
                        return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString() : (r?.perf_date || 'TBD');
                      },
                      getValue: (r) => r?.perf_date
                    },
                    { key: 'event_title', header: 'Event', widthClass: 'min-w-[160px]' },
                    { key: 'venue', header: 'Venue', widthClass: 'min-w-[140px]' },
                    {
                      key: 'city_state',
                      header: 'City',
                      widthClass: 'min-w-[130px]',
                      render: (r) => r?.city_state || [r?.city, r?.state].filter(Boolean).join(', ')
                    },
                    { key: 'status', header: 'Status', widthClass: 'min-w-[90px]' }
                  ]}
                />
              )}

              {activeTab === 'catalog' && (
                <DizeeTable
                  rows={catalog}
                  rowKey={(r) => r?._id}
                  emptyTitle="No catalog items found"
                  emptyBody="When you add songs/releases in Catalog, they’ll appear here."
                  columns={[
                    {
                      key: '__edit',
                      header: '',
                      widthClass: 'min-w-[70px]',
                      render: (r) => (
                        <button
                          onClick={() => r?._id && navigate(`/catalog/edit/${r._id}`)}
                          className="text-[10px] text-[#00CED1] hover:opacity-80"
                        >
                          Edit
                        </button>
                      )
                    },
                    { key: 'song_title', header: 'Title', widthClass: 'min-w-[180px]', getValue: (r) => r?.song_title || r?.title || r?.name },
                    { key: 'artist_name', header: 'Artist', widthClass: 'min-w-[140px]', getValue: (r) => r?.artist_name || r?.artist },
                    {
                      key: 'release_date',
                      header: 'Release date',
                      widthClass: 'min-w-[120px]',
                      render: (r) => {
                        const raw = r?.release_date;
                        if (!raw) return '';
                        const d = new Date(raw);
                        return !Number.isNaN(d.getTime()) ? d.toLocaleDateString() : String(raw);
                      },
                      getValue: (r) => r?.release_date
                    },
                    { key: 'status', header: 'Status', widthClass: 'min-w-[90px]' }
                  ]}
                />
              )}

              {activeTab === 'touring' && (
                <DizeeTable
                  rows={tourShows}
                  rowKey={(r) => r?._id}
                  emptyTitle="No tour shows found"
                  emptyBody="When you add tour shows in Tour Manager, they’ll appear here."
                  columns={[
                    {
                      key: '__open',
                      header: '',
                      widthClass: 'min-w-[70px]',
                      render: (r) => (
                        <button
                          onClick={() => r?._id && navigate(`/tour-manager/show/${r._id}`)}
                          className="text-[10px] text-[#00CED1] hover:opacity-80"
                        >
                          Open
                        </button>
                      )
                    },
                    {
                      key: 'showDate',
                      header: 'Date',
                      widthClass: 'min-w-[110px]',
                      render: (r) => {
                        const raw = r?.showDate;
                        if (!raw) return 'TBD';
                        const d = new Date(raw);
                        return !Number.isNaN(d.getTime()) ? d.toLocaleDateString() : String(raw);
                      },
                      getValue: (r) => r?.showDate
                    },
                    {
                      key: 'artist',
                      header: 'Artist',
                      widthClass: 'min-w-[140px]',
                      render: (r) => r?.showInfo?.artist || r?.artist || ''
                    },
                    {
                      key: 'venue',
                      header: 'Venue',
                      widthClass: 'min-w-[160px]',
                      render: (r) => r?.venue?.name || r?.venue || ''
                    },
                    {
                      key: 'city',
                      header: 'City',
                      widthClass: 'min-w-[140px]',
                      render: (r) => r?.venue?.city || r?.venue?.location || ''
                    }
                  ]}
                />
              )}

              {!loading && !isConfigured && (
                <button
                  onClick={() => navigate('/connectors/google-sheets')}
                  className="mt-4 w-full bg-[#00CED1] rounded-lg p-3 hover:bg-[#00B8B8] transition-all text-white text-[12px] font-medium"
                >
                  Connect Google Sheets
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </LayoutHeader>
  );
}


